import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { useEffect, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import '../styles/App.css';
import L, { Icon } from 'leaflet';
import IconMine from '../assets/icons/mineIcon.png';
import IconPit from '../assets/icons/pitIcon.png';
import MinePopup from '../components/MinePopup';
import MineEdit from '../components/MineEdit';
import { isTokenExpired } from '../api/auth';
import { getMines, addMine, updateMine, deleteMine } from '../api/mines';
import AddMineButton from '../components/AddMineButton';
import EditMineButton from '../components/EditMineButton';
import PopupMessage from '../components/PopupMessage';
import SearchBar from '../components/SearchBar';

export interface Mine {
  id: string;
  title: string;
  element_type: string;
  content: string;
  position: [number, number];
  images?: {
    id: string;
    src: string;
    comment: string;
  }[];
  links?: {
    id: string,
    url: string;
    title: string;
  }[];
}

interface PopUpInfo {
  message: string;
  type: string; // e.g., 'success', 'error', 'info'
}

// New custom icon - Mine
const customIconMine: Icon = new L.Icon({
  iconUrl: IconMine,
  iconSize: [32,32]
});

// New custom icon - Pit
const customIconPit: Icon = new L.Icon({
  iconUrl: IconPit,
  iconSize: [25,25]
});

// Center position
const centerPosition: [number, number] = [48.91007751360837, 17.117286887302956]; 


function Home() {
  const [mines, setMines] = useState<Mine[] | null>(null);
  const [selectedMine, setSelectedMine] = useState<Mine | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [addMode, setAddMode] = useState<boolean>(false);
  const [isLogged, setIsLogged] = useState<boolean>(false);
  const [popUpInfo, setPopUpInfo] = useState<PopUpInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [position, setPosition] = useState<[number, number] | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const currentToken = localStorage.getItem("jwt");
      setToken(currentToken);
      
      if(!isTokenExpired(currentToken ?? "")) {
        setIsLogged(true);
      }

      try {
        const data = await getMines();
        if (data) {
          setMines(data);
        } else {
          console.error("Failed to fetch mines data.");
          setPopUpInfo({
            message: "Nemůžeme načíst data prvků.",
            type: "error",
          });
        }
      } catch (error) {
        console.error("Error fetching mines:", error);
        setPopUpInfo({
          message: "Nemůžeme načíst data prvků.",
          type: "error",
        });
      }
    };

    fetchData();
  }, []);

  const handleAdd = async (newMine: Mine) => {
    try {
      const mine = await addMine(newMine, token ?? "");
      if (mine) {
        setMines([...(mines ?? []), mine]);
        setAddMode(false);
        setEditMode(true);
        setSelectedMine(mine);
        setPopUpInfo({
          message: "Prvek byl úspěšně přidán.",
          type: "success",
        });
      }
      else {
        console.error("Failed to add mine.");
        setPopUpInfo({
          message: "Chyba při přidávání prvku.",
          type: "error",
        });
      }
    } catch (error) {
      console.error('Failed to add mine:', error);
      setPopUpInfo({
            message: "Chyba při přidávání prvku.",
            type: "error",
      });
    }
  }

  const handleDelete = async (mineId: string) => {
    try {
      await deleteMine(mineId, token ?? "");
      setMines((prevMines) => prevMines?.filter(mine => mine.id !== mineId) ?? []);
      setPopUpInfo({
        message: "Důl byl úspěšně smazán.",
        type: "success",
      });
    } catch (error) {
      console.error('Failed to delete mine:', error);
      setPopUpInfo({
        message: "Chyba při mazání prvku.",
        type: "error",
      });
    }
  }

  const handleUpdate = async (mine: Mine) => {
    try {
      const updatedMine = await updateMine(mine, token ?? "");
      console.log("Updated mine:", updatedMine);
      if (updatedMine) {
        setMines((prevMines) => 
          prevMines?.map(m => m.id === updatedMine.id ? updatedMine : m) ?? []
        );
        setSelectedMine(null);
        setEditMode(false);
        setPopUpInfo({
          message: "Prvek byl úspěšně aktualizován.",
          type: "success",
        });
      }
    } catch (error) {
      console.error('Failed to update mine:', error);
      setPopUpInfo({
        message: "Chyba při aktualizaci prvku.",
        type: "error",
      });
    }
  }

  const handleResultClick = (lat: number, lng: number) => {
    const newPosition: [number, number] = [lat, lng];
    setPosition(newPosition);
  }

  function ClickHandler() {
    useMapEvents({
      click: async (e) => {
        // If in add mode, create a new mine at clicked position
        if (addMode && isLogged) {
          const newMine: Mine = {
            id: '',
            title: 'Nový prvek',
            element_type: 'mine',
            content: 'Popis nového prvku...',
            position: [e.latlng.lat, e.latlng.lng],
          };

          await handleAdd(newMine);
        }
      }
    });
    return null;
  }

  // Change map view
  function ChangeMapView({position}: {position: [number, number]}) {
    const map = useMap();
    map.flyTo(position, 15);
    setPosition(null);
    return null;
  }

  // Render modal based on selected mine and edit mode
  const renderModal = () => {
    if (selectedMine && editMode) {
      return (
        <MineEdit 
          data={selectedMine} 
          onClose={() => setSelectedMine(null)} 
          onSave={handleUpdate}
          onDelete={() => selectedMine && handleDelete(selectedMine.id)}
        />
      );
    } else if (selectedMine) {
      return (
        <MinePopup 
          data={selectedMine} 
          onClose={() => setSelectedMine(null)} 
        />
      );
    }
    return null;
  };

  return (
    <>
      <MapContainer center={centerPosition} zoom={13} style={{ height: '100vh', width: '100%' }}>
        <TileLayer 
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
          attribution='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <ClickHandler/>
        {mines?.map((mine: Mine) => (
          <Marker
            key={mine.id}
            position={mine.position}
            icon={mine.element_type === 'mine' ? customIconMine : customIconPit}
            eventHandlers={{
              click: () => {
                setSelectedMine(mine);
              }
            }}
          ></Marker>
        ))}
        {/* Change map view based on position */}
        {position && <ChangeMapView position={position} />}
      </MapContainer>
      
      {/* Floating action buttons (Only User) */}
      { isLogged && (
        <div className="floating-buttons">
          <EditMineButton onClick={setEditMode} editMode={editMode} />
          <AddMineButton onClick={setAddMode} addMode={addMode} />
        </div>
      )}
      
      {renderModal()}

      {/* Search Bar */}
      {!selectedMine && (
      <div className="search-bar-top-center">
        <SearchBar 
          mines={mines ?? []}
          onResultSelect={handleResultClick}
        />
      </div>
      )}

      {/* Pop-up info message */}
      {popUpInfo && (
        <PopupMessage message={popUpInfo.message} type={popUpInfo.type} onClose={() => setPopUpInfo(null)} />
      )}
    </>
  );
}

export default Home;
